import { Modal, Form, Select, DatePicker, Input, Descriptions, Alert, Button, message } from '../../ui';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';

const MODES = ['UPI', 'NEFT', 'RTGS', 'IMPS', 'CASH', 'CHEQUE', 'CARD', 'OTHER'];
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Record a REFUND of an advance whose order was cancelled/rejected. The platform only TRACKS a refund the
 * accountant already made outside it — attach the reversal proof. Posts the compensating ledger entry so the
 * retailer's balance nets to zero. Accounts/Admin/SA only (gated by PAYMENT.REFUND on the caller).
 */
export function RefundPaymentModal({ payment, orderNo, onClose, onDone }: {
  payment: any | null; orderNo?: string; onClose: () => void; onDone: () => void;
}) {
  const [form] = Form.useForm();
  const open = !!payment;

  const submit = useMutation({
    mutationFn: (v: any) => request<any>({
      url: `/payments/${payment.id}/refund`, method: 'POST',
      data: {
        refundMode: v.refundMode,
        refundDate: v.refundDate.format('YYYY-MM-DD'),
        refundReason: v.refundReason,
        refundProofUrls: v.refundProofUrls,
      },
    }),
    onSuccess: () => { message.success('Refund recorded'); onDone(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to record refund'),
  });

  const proof = payment ? (payment.attachmentUrls?.[0] || payment.attachmentUrl) : null;
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={`Process refund — ${payment?.paymentNo ?? ''}`} width={620}>
      {payment && (
        <>
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Advance">{inr2(payment.amount)} · {payment.mode}</Descriptions.Item>
            <Descriptions.Item label="Order">{orderNo ?? payment.orderNo ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Original proof">{proof ? <a href={proof} target="_blank" rel="noopener">view</a> : '—'}</Descriptions.Item>
          </Descriptions>
          <Alert type="info" style={{ marginBottom: 12 }}
            title="Recording a refund you have already made outside the platform"
            description="Verify the original payment against your bank first. This captures the reversal + proof and returns the retailer's balance to zero — the platform does not move any money." />
          <Form key={payment.id} form={form} layout="vertical"
            initialValues={{ refundMode: payment.mode ?? 'UPI', refundDate: dayjs(), refundReason: undefined, refundProofUrls: undefined }}
            onFinish={(v) => submit.mutate(v)}>
            <Form.Item name="refundMode" label="Refund mode" rules={[{ required: true }]}>
              <Select options={MODES.map((m) => ({ value: m }))} />
            </Form.Item>
            <Form.Item name="refundDate" label="Refund date" rules={[{ required: true }]}>
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item name="refundReason" label="Reason / notes">
              <Input.TextArea rows={2} maxLength={255} placeholder="e.g. Order cancelled by retailer — advance returned via UPI" />
            </Form.Item>
            <Form.Item name="refundProofUrls" label="Refund proof"
              rules={[{ required: true, type: 'array', min: 1, message: 'Attach the reversal / transfer receipt' }]}
              extra="Required — the reversal screenshot or receipt.">
              <MultiFileUploadField category="receipt" buttonLabel="Upload refund proof" />
            </Form.Item>
            <Button variant="danger" type="submit" loading={submit.isPending} block>
              Record refund of {inr2(payment.amount)}
            </Button>
          </Form>
        </>
      )}
    </Modal>
  );
}
